import { Component, type ReactNode } from 'react'
import { withTranslation, type WithTranslation } from 'react-i18next'

import { btnPrimary } from './ui'

const RELOAD_STAMP_KEY = 'chunkReloadAt'
const RELOAD_WINDOW_MS = 30_000

/** A chunk-load failure's message, from either Vite's `preloadError` payload or the browser's own dynamic-import rejection. */
export function isChunkLoadError(message: string | null | undefined): boolean {
  if (!message) return false
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  )
}

export function shouldReload(now: number, lastStamp: number | null): boolean {
  return lastStamp === null || now - lastStamp > RELOAD_WINDOW_MS
}

function readReloadStamp(): number | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_STAMP_KEY)
    return raw ? Number(raw) : null
  } catch {
    return null
  }
}

export function reloadConfirmed(now: number, readBack: number | null): boolean {
  return readBack === now
}

/** Returns false -- never throws -- on any failure: `setItem` throwing, `getItem` throwing, or a confirmed-different read-back. */
function writeReloadStamp(now: number): boolean {
  try {
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(now))
    const raw = window.sessionStorage.getItem(RELOAD_STAMP_KEY)
    return reloadConfirmed(now, raw ? Number(raw) : null)
  } catch {
    return false
  }
}

export function reloadOnceForChunkError(): boolean {
  const now = Date.now()
  if (!shouldReload(now, readReloadStamp())) return false
  if (!writeReloadStamp(now)) return false
  window.location.reload()
  return true
}

/** The crash text: the page is named only when it was known at the moment of the crash. */
export function crashTitle(page: string | null, t: (key: string, options?: Record<string, unknown>) => string): string {
  return page ? t('error.pageStopped', { page }) : t('error.title')
}

type Props = WithTranslation & { children: ReactNode; page?: string }
type State = { hasError: boolean; message: string; page: string | null }

class ErrorBoundaryBase extends Component<Props, State> {
  state: State = { hasError: false, message: '', page: null }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { hasError: true, message: error instanceof Error ? error.message : String(error) }
  }

  componentDidCatch(error: Error): void {
    this.setState({ page: this.props.page ?? null })
    if (isChunkLoadError(error.message)) reloadOnceForChunkError()
  }

  render() {
    if (this.state.hasError) {
      const { t } = this.props
      const { message, page } = this.state
      return (
        <div role="alert" className="flex h-screen w-full flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">{crashTitle(page, t)}</p>
          <p className="max-w-prose text-sm text-slate-600 dark:text-slate-300">{t('error.next')}</p>
          <button type="button" onClick={() => window.location.reload()} className={btnPrimary}>
            {t('error.reload')}
          </button>
          {message && (
            <details className="w-full max-w-prose text-left text-sm">
              <summary className="cursor-pointer text-slate-600 dark:text-slate-300">{t('error.details')}</summary>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-100 p-3 text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                {message}
              </pre>
            </details>
          )}
        </div>
      )
    }
    return this.props.children
  }
}

const ErrorBoundary = withTranslation()(ErrorBoundaryBase)
export default ErrorBoundary
